import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { ExpandedState } from "@tanstack/react-table";
import { Form1099ReportTransactionLine } from "@common/schemas/domains/tax/form1099-report.schema";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { View } from "@common/types/views/views";
import { Form1099NecView } from "@common/types/views/form1099NecView";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { ViewConfig } from "@lib/views";
import generalLedgerStyles from "../generalLedger/GeneralLedgerTable.module.scss";
import { useGlSourceDrilldown } from "../generalLedger/useGlSourceDrilldown";
import { useEntitiesById } from "@hooks/useEntities";
import { useOverlays } from "contexts/OverlayContext";
import { Form1099NecEmptyState } from "./Form1099NecEmptyState";
import { Form1099NecRow, Form1099TableMeta } from "./form1099NecFormat";
import styles from "./Form1099NecTable.module.scss";

interface Props {
    rows: Form1099NecRow[];
    isFiltered: boolean;
    onViewVendors?: () => void;
    onUpdateMapping?: () => void;
    view: Form1099NecView;
    config: ViewConfig<Form1099NecRow, string, never>;
    onViewChange: (view: View) => void;
    tableRef?: React.Ref<ViewTableHandle>;
    expanded: ExpandedState;
    onExpandedChange: React.Dispatch<React.SetStateAction<ExpandedState>>;
}

export const Form1099NecTable: React.FC<Props> = ({
    rows,
    isFiltered,
    onViewVendors,
    onUpdateMapping,
    view,
    config,
    onViewChange,
    tableRef,
    expanded,
    onExpandedChange,
}) => {
    const { openOverlay } = useOverlays();
    const entitiesById = useEntitiesById();
    const onSourceClick = useGlSourceDrilldown({
        basis: AccountingLedgerBasis.cash,
    });

    const onReferenceClick = useCallback(
        (transaction: Form1099ReportTransactionLine) => {
            const entity = entitiesById.get(transaction.entityId);
            if (!entity) {
                return;
            }
            openOverlay("journalEntryDrawer", {
                entity,
                journalEntryId: transaction.referenceNumber,
                ledgerBasis: AccountingLedgerBasis.cash,
            });
        },
        [entitiesById, openOverlay],
    );

    const onRowClick = useCallback(
        (row?: Form1099NecRow) => {
            if (!row || row.rowKind !== "transaction" || !row.transaction) {
                return;
            }
            onReferenceClick(row.transaction);
        },
        [onReferenceClick],
    );

    const tableMeta: Form1099TableMeta = useMemo(
        () => ({ callbacks: { onSourceClick, onReferenceClick } }),
        [onSourceClick, onReferenceClick],
    );

    return (
        <ViewTable
            ref={tableRef}
            data={rows}
            view={view}
            config={config}
            tableMeta={tableMeta}
            onRowClick={onRowClick}
            onViewChange={onViewChange}
            className={classNames(
                generalLedgerStyles.generalLedgerTable,
                styles.form1099NecTable,
            )}
            rowBorders={false}
            enableColumnResizing
            expanded={expanded}
            onExpandedChange={onExpandedChange}
            nestingIndentPx={16}
            fixedTopOffsetPx
            emptyView={() => (
                <Form1099NecEmptyState
                    isFiltered={isFiltered}
                    onViewVendors={onViewVendors}
                    onUpdateMapping={onUpdateMapping}
                />
            )}
        />
    );
};
